import React from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/api";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, CartesianGrid } from "recharts";
import { AlertTriangle, TrendingUp, TrendingDown, Wallet, CalendarClock } from "lucide-react";
import RateSettimanaleTab from "@/pages/cashflow/RateSettimanaleTab";
import CashFlowAlertConfig from "@/pages/cashflow/CashFlowAlertConfig";

const eur = (n) => new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n || 0);

const TABS = [
  { key: "proiezioni", label: "Proiezioni", icon: TrendingUp },
  { key: "rate_settimanale", label: "Rate fornitori · settimanale", icon: CalendarClock },
];

export default function CashFlow() {
  const [tab, setTab] = React.useState(() => {
    const p = new URLSearchParams(window.location.search);
    return p.get("tab") || "proiezioni";
  });

  React.useEffect(() => {
    const url = new URL(window.location.href);
    if (tab === "proiezioni") url.searchParams.delete("tab");
    else url.searchParams.set("tab", tab);
    window.history.replaceState({}, "", url.toString());
  }, [tab]);

  return (
    <div className="space-y-6">
      <div>
        <div className="overline">Situazione finanziaria</div>
        <h1 className="font-display text-3xl xl:text-4xl font-black">Cash Flow & Previsioni</h1>
        <p className="text-slate-600 text-sm mt-1">Proiezioni automatiche basate su fatture, bolle e costi fissi.</p>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 border-b border-slate-200" data-testid="cashflow-tabs">
        {TABS.map((t) => {
          const Icon = t.icon;
          const active = tab === t.key;
          return (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              data-testid={`tab-${t.key}`}
              className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-bold border-b-2 -mb-px transition-colors ${
                active
                  ? "border-orange-600 text-orange-700"
                  : "border-transparent text-slate-500 hover:text-slate-900 hover:border-slate-300"
              }`}
            >
              <Icon className="w-4 h-4" />
              {t.label}
            </button>
          );
        })}
      </div>

      {tab === "proiezioni" && <ProiezioniPanel />}
      {tab === "rate_settimanale" && (
        <>
          <CashFlowAlertConfig />
          <RateSettimanaleTab />
        </>
      )}
    </div>
  );
}

function ProiezioniPanel() {
  const queryClient = useQueryClient();
  const [saldo, setSaldo] = React.useState("");
  const [errore, setErrore] = React.useState("");
  const salvaSaldo = useMutation({ mutationFn: (valore) => api.put("/admin/cashflow/saldo-iniziale", { saldo_iniziale: valore }), onSuccess: () => { queryClient.invalidateQueries({ queryKey: ["cashflow"] }); setErrore(""); }, onError: () => setErrore("Impossibile salvare il saldo") });
  const { data, isLoading } = useQuery({
    queryKey: ["cashflow"],
    queryFn: () => api.get("/admin/cashflow").then((r) => r.data),
  });

  React.useEffect(() => { if (data) setSaldo(String(data.saldo_iniziale ?? 0)); }, [data?.saldo_iniziale]);

  if (isLoading || !data) return <div className="text-slate-500">Caricamento…</div>;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <div className="bg-white border border-slate-200 p-4"><div className="overline">Saldo bancario attuale</div><form className="mt-2 flex flex-wrap gap-2" onSubmit={(e) => { e.preventDefault(); const n = Number(saldo.replace(",", ".")); if (!saldo.trim() || !Number.isFinite(n)) { setErrore("Inserisci un importo valido"); return; } salvaSaldo.mutate(n); }}><input aria-label="Saldo bancario attuale in euro" type="text" inputMode="decimal" value={saldo} onChange={(e) => setSaldo(e.target.value)} className="w-32 min-w-0 border border-slate-300 rounded px-2 py-1 font-bold" /><button type="submit" disabled={salvaSaldo.isPending} className="bg-orange-600 text-white rounded px-3 py-1 text-sm font-bold disabled:opacity-50">{salvaSaldo.isPending ? "Salvataggio..." : "Salva saldo"}</button></form>{errore && <div className="text-red-600 text-xs mt-1">{errore}</div>}<p className="text-xs text-slate-500 mt-2">Inserisci il saldo disponibile oggi, gia comprensivo degli incassi ricevuti e dei pagamenti effettuati.</p></div>
        <CreditiDettaglio totale={data.entrate_attese_totali} />
        <DettaglioCashflow titolo="Entrate future lavori" totale={data.entrate_future_lavori} righe={data.dettagli_lavori || []} icon={Wallet} />
        <DettaglioCashflow titolo="Uscite fornitori" totale={data.uscite_fornitori} righe={data.dettagli_fornitori || []} icon={TrendingDown} />
        <DettaglioCashflow titolo="Costi di gestione mensili" totale={data.costi_fissi_mensili} righe={data.dettagli_costi_fissi || []} icon={TrendingDown} />
      </div>

      <div className="bg-white border border-slate-200 p-5">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="overline">Proiezioni cash flow</div>
            <h3 className="font-display font-bold text-lg">Andamento a 7/30/60/90/180/365 giorni</h3>
          </div>
        </div>
        <div className="w-full h-72">
          <ResponsiveContainer>
            <BarChart data={data.proiezioni}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="giorni" tick={{ fontSize: 11 }} tickFormatter={(v) => `${v}g`} />
              <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
              <Tooltip formatter={(v) => eur(v)} />
              <Legend />
              <Bar dataKey="entrate_previste" fill="#16a34a" name="Entrate previste" />
              <Bar dataKey="uscite_previste" fill="#dc2626" name="Uscite previste" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="bg-white border border-slate-200">
        <div className="p-4 border-b border-slate-200"><div className="overline">Dettaglio proiezioni</div></div>
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-600">
            <tr>
              <th className="text-left p-3">Orizzonte</th>
              <th className="text-right p-3">Entrate previste</th>
              <th className="text-right p-3">Uscite previste</th>
              <th className="text-right p-3">Saldo progressivo</th>
              <th className="text-left p-3">Stato</th>
            </tr>
          </thead>
          <tbody>
            {data.proiezioni.map((p) => (
              <tr key={p.giorni} className="border-t border-slate-100">
                <td className="p-3 font-mono font-bold">{p.giorni} giorni</td>
                <td className="p-3 text-right font-mono text-emerald-700">{eur(p.entrate_previste)}</td>
                <td className="p-3 text-right font-mono text-red-700">{eur(p.uscite_previste)}</td>
                <td className={`p-3 text-right font-mono font-bold ${p.saldo_progressivo >= 0 ? "text-emerald-700" : "text-red-700"}`}>
                  {eur(p.saldo_progressivo)}
                </td>
                <td className="p-3">
                  {p.saldo_progressivo < 0 ? (
                    <span className="inline-flex items-center gap-1 text-[10px] uppercase font-bold px-2 py-0.5 bg-red-100 text-red-800 border border-red-200 rounded-sm">
                      <AlertTriangle className="w-3 h-3" /> Rischio liquidità
                    </span>
                  ) : (
                    <span className="text-[10px] uppercase font-bold px-2 py-0.5 bg-emerald-100 text-emerald-800 border border-emerald-200 rounded-sm">
                      OK
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {data.proiezioni.some((p) => p.saldo_progressivo < 0) && (
        <div className="p-4 bg-red-50 border border-red-200 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-red-700 mt-0.5" />
          <div>
            <div className="font-bold text-red-900">Attenzione: previsione di saldo negativo</div>
            <div className="text-sm text-red-800">
              Verifica scadenze, accelera incassi o pianifica un finanziamento a breve termine.
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value, icon: Icon, good, bad, highlight }) {
  return (
    <div className={`bg-white border p-4 ${highlight ? "border-orange-500" : "border-slate-200"}`}>
      <div className="flex items-center justify-between">
        <div className="overline">{label}</div>
        {Icon && <Icon className={`w-4 h-4 ${good ? "text-emerald-600" : bad ? "text-red-600" : "text-slate-400"}`} />}
      </div>
      <div className="font-display font-black text-xl mt-1">{value}</div>
    </div>
  );
}

function CreditiDettaglio({ totale }) {
  const [aperto, setAperto] = React.useState(false);
  const { data = [], isLoading, isError } = useQuery({
    queryKey: ["fatture-crediti"],
    queryFn: () => api.get("/fatture").then(r => r.data),
    enabled: aperto,
  });
  const fatture = (Array.isArray(data) ? data : []).filter(f =>
    ["emessa", "parzialmente_incassata", "scaduta"].includes(f.stato) &&
    Number(f.importo_residuo || 0) > 0
  );
  return (
    <>
      <button type="button" onClick={() => setAperto(true)} className="text-left w-full cursor-pointer">
        <StatCard label="Entrate attese (crediti) · Vedi fatture" value={eur(totale)} icon={TrendingUp} good />
      </button>
      {aperto && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setAperto(false)}>
          <div role="dialog" aria-modal="true" className="bg-white rounded-lg p-5 w-full max-w-5xl max-h-[85vh] overflow-auto" onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">Fatture ancora da incassare</h2>
              <button onClick={() => setAperto(false)} className="border rounded px-3 py-1">Chiudi</button>
            </div>
            {isLoading ? <p>Caricamento...</p> : isError ? <p className="text-red-600">Errore caricamento fatture</p> : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr className="border-b">
                    {["Numero", "Cliente", "Emissione", "Incasso previsto", "Totale", "Incassato", "Residuo", "Stato"].map(x => <th key={x} className="p-2 text-left">{x}</th>)}
                  </tr></thead>
                  <tbody>{fatture.map((f, i) => (
                    <tr key={f.fattura_id || i} className="border-b">
                      <td className="p-2">{f.numero || f.numero_fattura || "—"}</td>
                      <td className="p-2">{f.cliente || "—"}</td>
                      <td className="p-2">{f.data_emissione || "—"}</td>
                      <td className="p-2">{f.data_incasso_prevista || "—"}</td>
                      <td className="p-2">{eur(f.importo_totale)}</td>
                      <td className="p-2">{eur(f.importo_incassato)}</td>
                      <td className="p-2 font-bold">{eur(f.importo_residuo)}</td>
                      <td className="p-2">{f.stato}</td>
                    </tr>
                  ))}</tbody>
                </table>
                <p className="text-right font-bold mt-4">Totale da incassare: {eur(fatture.reduce((s, f) => s + Number(f.importo_residuo || 0), 0))}</p>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}

function DettaglioCashflow({ titolo, totale, righe, icon }) {
  const [aperto, setAperto] = React.useState(false);
  return (
    <>
      <button type="button" onClick={() => setAperto(true)} className="w-full text-left cursor-pointer">
        <StatCard label={`${titolo} · Vedi dettagli`} value={eur(totale)} icon={icon} />
      </button>
      {aperto && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setAperto(false)}>
          <div role="dialog" aria-modal="true" className="bg-white rounded-lg p-5 w-full max-w-4xl max-h-[85vh] overflow-auto" onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">{titolo}</h2>
              <button onClick={() => setAperto(false)} className="border rounded px-3 py-1">Chiudi</button>
            </div>
            <table className="w-full text-sm">
              <thead><tr className="border-b"><th className="p-2 text-left">Tipo</th><th className="p-2 text-left">Descrizione</th><th className="p-2 text-left">Data / Periodicità</th><th className="p-2 text-right">Importo</th></tr></thead>
              <tbody>
                {righe.map((r, i) => <tr key={i} className="border-b">
                  <td className="p-2">{r.tipo || "—"}</td>
                  <td className="p-2">{r.descrizione || "—"}</td>
                  <td className="p-2">{r.data || "—"}</td>
                  <td className="p-2 text-right">{eur(r.importo)}</td>
                </tr>)}
              </tbody>
            </table>
            <p className="text-right font-bold mt-4">Totale: {eur(righe.reduce((s, r) => s + Number(r.importo || 0), 0))}</p>
          </div>
        </div>
      )}
    </>
  );
}
